import { supabase } from "../lib/supabase";

export class SubitemTemplateService {
  static transformTemplateData(template) {
    return {
      id: template.id,
      name: template.name,
      phase: template.phase,
      order: template.order_number,
      revisionVersion: template.assignee,  // Using assignee field to store revision version
      revisionType: template.revision_type,  // revision_type column ('base', 'revision', 'both')
      templateFilters: template.template_filters, // Keep for backwards compatibility
      categoryIds: template.template_category_assignments?.map(a => a.category_id) || [],
      createdAt: template.created_at
    };
  }

  static async getAllSubitemTemplates() {
    try {
      const { data, error } = await supabase
        .from('subitem_templates')
        .select(`
          *,
          template_category_assignments (
            category_id
          )
        `)
        .order('order_number', { ascending: true });

      if (error) {
        console.error('Error fetching subitem templates:', error);
        throw new Error(`Failed to fetch subitem templates: ${error.message}`);
      }

      return data?.map(this.transformTemplateData) || [];
    } catch (error) {
      console.error('Error in getAllSubitemTemplates:', error);
      throw error;
    }
  }

  static async createSubitemTemplate(templateData) {
    try {
      const dbData = {
        name: templateData.name,
        phase: templateData.phase,
        order_number: templateData.order,
        assignee: templateData.revisionVersion,  // Using assignee field to store revision version
        revision_type: templateData.revisionType || 'base',
        template_filters: templateData.templateFilters || null
      };

      const { data, error } = await supabase
        .from('subitem_templates')
        .insert([dbData])
        .select()
        .single();

      if (error) {
        console.error('Error creating subitem template:', error);
        throw new Error(`Failed to create subitem template: ${error.message}`);
      }

      // If categoryIds provided, save to template_category_assignments
      if (templateData.categoryIds && templateData.categoryIds.length > 0) {
        await this.updateTemplateCategoryAssignments(data.id, templateData.categoryIds);
      }

      // Return with categoryIds
      return {
        ...this.transformTemplateData(data),
        categoryIds: templateData.categoryIds || []
      };
    } catch (error) {
      console.error('Error in createSubitemTemplate:', error);
      throw error;
    }
  }

  static async updateSubitemTemplate(id, templateData) {
    try {
      const dbData = {};

      // Only include fields that are provided
      if (templateData.name !== undefined) dbData.name = templateData.name;
      if (templateData.phase !== undefined) dbData.phase = templateData.phase;
      if (templateData.order !== undefined) dbData.order_number = templateData.order;
      if (templateData.revisionVersion !== undefined) dbData.assignee = templateData.revisionVersion;
      if (templateData.revisionType !== undefined) dbData.revision_type = templateData.revisionType;
      if (templateData.templateFilters !== undefined) dbData.template_filters = templateData.templateFilters;

      // Only update if there are fields to update
      let data = null;
      if (Object.keys(dbData).length > 0) {
        const result = await supabase
          .from('subitem_templates')
          .update(dbData)
          .eq('id', id)
          .select()
          .single();

        if (result.error) {
          console.error('Error updating subitem template:', result.error);
          throw new Error(`Failed to update subitem template: ${result.error.message}`);
        }
        data = result.data;
      }

      // If categoryIds provided, update template_category_assignments
      if (templateData.categoryIds !== undefined) {
        await this.updateTemplateCategoryAssignments(id, templateData.categoryIds);
      }

      // Fetch updated template with category assignments
      if (!data) {
        const { data: fetchedData } = await supabase
          .from('subitem_templates')
          .select('*')
          .eq('id', id)
          .single();
        data = fetchedData;
      }

      return {
        ...this.transformTemplateData(data),
        categoryIds: templateData.categoryIds || []
      };
    } catch (error) {
      console.error('Error in updateSubitemTemplate:', error);
      throw error;
    }
  }

  /**
   * Update category assignments for a template (replace all)
   */
  static async updateTemplateCategoryAssignments(templateId, categoryIds) {
    try {
      // Delete existing assignments
      const { error: deleteError } = await supabase
        .from('template_category_assignments')
        .delete()
        .eq('template_id', templateId);

      if (deleteError) {
        console.error('Error deleting template category assignments:', deleteError);
        throw new Error(`Failed to update template categories: ${deleteError.message}`);
      }

      // Insert new assignments (if any)
      if (categoryIds && categoryIds.length > 0) {
        const assignments = categoryIds.map(categoryId => ({
          template_id: templateId,
          category_id: categoryId
        }));

        const { error: insertError } = await supabase
          .from('template_category_assignments')
          .insert(assignments);

        if (insertError) {
          console.error('Error inserting template category assignments:', insertError);
          throw new Error(`Failed to update template categories: ${insertError.message}`);
        }
      }

      return true;
    } catch (error) {
      console.error('Error in updateTemplateCategoryAssignments:', error);
      throw error;
    }
  }

  static async deleteSubitemTemplate(id) {
    try {
      // Delete category assignments first (cascade should handle this, but be explicit)
      await supabase
        .from('template_category_assignments')
        .delete()
        .eq('template_id', id);

      const { error } = await supabase
        .from('subitem_templates')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting subitem template:', error);
        throw new Error(`Failed to delete subitem template: ${error.message}`);
      }

      return true;
    } catch (error) {
      console.error('Error in deleteSubitemTemplate:', error);
      throw error;
    }
  }

  static async getTemplatesByProjectType(projectType) {
    try {
      const { data, error } = await supabase
        .from('subitem_templates')
        .select('*')
        .contains('template_filters', [projectType])
        .order('order_number', { ascending: true });

      if (error) {
        console.error('Error fetching templates by project type:', error);
        throw new Error(`Failed to fetch templates: ${error.message}`);
      }

      return data?.map(this.transformTemplateData) || [];
    } catch (error) {
      console.error('Error in getTemplatesByProjectType:', error);
      throw error;
    }
  }

  static async batchUpdateOrderNumbers(updates) {
    try {
      // updates = [{id: 1, order_number: 10}, {id: 2, order_number: 20}, ...]
      const promises = updates.map(update =>
        supabase
          .from('subitem_templates')
          .update({ order_number: update.order_number })
          .eq('id', update.id)
      );

      const results = await Promise.all(promises);
      const errors = results.filter(r => r.error);

      if (errors.length > 0) {
        console.error('Errors updating order numbers:', errors);
        throw new Error(`Failed to update ${errors.length} template order numbers`);
      }

      return true;
    } catch (error) {
      console.error('Error in batchUpdateOrderNumbers:', error);
      throw error;
    }
  }
}
